import { useEffect, useState } from "react";
import { Menu, Moon, Sun, X } from "lucide-react";
import Logo from "@/components/Logo";
import { PROPERTY } from "@/data/property";
import { useSite } from "@/context/SiteContext";
import { scrollToId } from "@/lib/smooth";

const LINKS = [
  { id: "overview", label: "Overview", testid: "nav-link-overview" },
  { id: "gallery", label: "Gallery", testid: "nav-link-gallery" },
  { id: "tour", label: "3D Tour & Plans", testid: "nav-link-tour" },
  { id: "neighbourhood", label: "Neighbourhood", testid: "nav-link-neighbourhood" },
  { id: "contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const { currency, setCurrency, currencies } = useSite();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="navbar"
      className="fixed inset-x-0 top-0 z-50 border-b border-zinc-200/50 bg-white/80 backdrop-blur-xl dark:border-zinc-800/50 dark:bg-black/70"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button onClick={() => go("overview")} data-testid="nav-brand-button" aria-label="Back to top">
          <Logo />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-zinc-600 transition-colors duration-200 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          {PROPERTY.priceCad != null && (
            <div
              data-testid="currency-switcher"
              role="group"
              aria-label="Currency"
              className="flex items-center rounded-full border border-zinc-200 p-0.5 dark:border-zinc-700"
            >
              {currencies.map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors duration-200 ${
                    currency === c
                      ? "bg-zinc-900 text-white dark:bg-white dark:text-black"
                      : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          <button
            data-testid="theme-toggle"
            onClick={() => setDark((d) => !d)}
            aria-label="Toggle theme"
            className="rounded-full border border-zinc-200 p-2 text-zinc-600 transition-colors duration-200 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:text-white"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            data-testid="schedule-tour-header-button"
            onClick={() => go("contact")}
            className="rounded-full bg-zinc-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bronze dark:bg-white dark:text-black dark:hover:bg-bronze dark:hover:text-white"
          >
            Schedule Tour
          </button>
        </div>

        <button
          className="rounded-full border border-zinc-200 p-2 text-zinc-700 dark:border-zinc-700 dark:text-zinc-200 lg:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-zinc-200/60 bg-white/95 px-4 py-4 backdrop-blur-xl dark:border-zinc-800 dark:bg-black/90 lg:hidden">
          <nav className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => go(l.id)}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-zinc-700 transition-colors duration-200 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("contact")}
              data-testid="schedule-tour-header-button-mobile"
              className="mt-3 rounded-full bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bronze"
            >
              Schedule Tour
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
